import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { setTitle } from "../../../../helpers/MetaTag";
import { useDebounce } from "../../../../hooks/useDebounce";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getPaginationRange, handleFilterChange } from "../../../../helpers";
import Table from "../../../../components/elements/table/Table";
import Button from "../../../../components/form/Button";
import DeleteModal from "../../../../components/common/DeleteModal";
import { useNavigate } from "react-router-dom";
import TableControls from "../../../../components/elements/TableControls";
import ActionBar from "../../../../components/elements/ActionBar";
import { useLoadTaxonomyList } from "../../../../helpers/GetCommonList";
import { tableDelete, tableList } from "../../../../services/tableService";
import Badge from "../../../../components/elements/Badge";
import Drawer from "../../../../components/drawer/Drawer";
import TableDetails from "./TableDetails";
import ManageTable from "../../../../components/sections/admin/Table/ManageTable";

const TableList = () => {
  setTitle("Table");
  const navigate = useNavigate();
  const [selectedItems, setSelectedItems] = useState([]);
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const queryClient = useQueryClient();
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    data: null,
  });
  const [drawerOpen, setDrawerOpen] = useState({
    isOpen: false,
    tableId: null,
  });
  const [manageDrawerOpen, setManageDrawerOpen] = useState({
    isOpen: false,
    data: {},
  });
  const [filters, setFilters] = useState({
    limit: 100,
    page: 1,
    sortQuery: "ordering",
  });

  const statusArray = [
    {
      state: "paid",
      color: "success",
    },
    {
      state: "pending",
      color: "warning",
    },
    {
      state: "partial",
      color: "info",
    },
  ];

  const [selectedFilter, setSelectedFilter] = useState(null);
  const [selectFilterItem, setSelectFilterItem] = useState([]);
  const [selectedAllFilters, setSelectedAllFilters] = useState([]);
  const userLang = localStorage.getItem("lang") || "en";

  const [selectedSearchData, setSelectedSearchData] = useState({
    category: [],
  });

  const { loadTaxonomyList } = useLoadTaxonomyList();
  const [categorieslist, setCategoriesList] = useState({
    data: [],
    loading: true,
  });
  const [categoryKeyWord, setCategoryKeyWord] = useState("");

  useEffect(() => {
    loadTaxonomyList({ type: "post-category", keyword: categoryKeyWord || "" }, setCategoriesList);
  }, [loadTaxonomyList, categoryKeyWord]);

  const { data, error, isLoading, updateData, isFetching, refetch } = useQuery({
    queryKey: ["table", filters, search],
    queryFn: async () => {
      const res = await tableList({
        ...filters,
        ...(search && { keyword: search }),
        ...(filters.orderStatuses?.length > 0 && Object.fromEntries(filters.orderStatuses.map((val, index) => [`orderStatuses[${index}]`, val]))),
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
  });

  const tableHeadData = [
    {
      _id: 1,
      name: "Table Number",
      align: "left",
    },
    {
      _id: 2,
      name: "Table Name",
      align: "left",
    },
    {
      _id: 3,
      name: "QR Code",
      align: "left",
    },
    {
      _id: 4,
      name: "Current Bill",
      align: "left",
    },
    {
      _id: 5,
      name: "Payment Status",
      align: "left",
      isFilter: true,
      isFilterSearch: false,
      onFilter: (val) => {
        if (val?.length > 0) {
          setFilters((pre) => ({
            ...pre,
            orderStatuses: val.map((item) => item.value),
            page: 1,
          }));
        } else {
          setFilters((pre) => {
            const updated = { ...pre };
            delete updated.orderStatuses;
            return updated;
          });
        }
      },
      filterData:
        [
          { name: "Pending", value: "pending", type: "checkbox" },
          { name: "Partial", value: "partial", type: "checkbox" },
        ] || [],
    },
    {
      _id: 6,
      name: "Action",
      align: "left",
    },
  ];

  const tableData = data?.data?.map((item) => {
    return {
      _id: item?._id,
      rowData: [
        {
          _id: 1,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.tableNumber,
        },
        {
          _id: 2,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: (
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-yellow-100 via-orange-50 to-pink-100 text-orange-700 font-semibold shadow-inner border border-orange-200">
              <i className="fa-solid fa-utensils text-orange-500"></i>
              {item?.tableName}
            </span>
          ),
        },
        {
          _id: 3,
          type: "user",
          align: "left",
          cellClass: "!min-w-[100px]",
          image: item?.qrImageUrl,
          onClick: () => {
            setDrawerOpen({
              isOpen: true,
              tableId: item?._id,
            });
          },
        },
        {
          _id: 4,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.orderTotal > 0 ? `$ ${item?.orderTotal}` : "",
        },
        {
          _id: 5,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.orderStatus ? (
            <div className="flex gap-2 items-center cursor-pointer">
              <Badge
                type={statusArray.find((st) => st.state === item?.orderStatus)?.color}
                label={statusArray.find((st) => st.state === item?.orderStatus)?.state}
              />
            </div>
          ) : (
            ""
          ),
        },
        {
          _id: 6,
          type: "action",
          align: "left",
          cellClass: "w-[5%]",
          actionData: [
            {
              _id: 1,
              name: "Details",
              icon: "fa-regular fa-eye",
              onClick: () => {
                setDrawerOpen({
                  isOpen: true,
                  tableId: item?._id,
                });
              },
              standout: false,
            },

            {
              _id: 2,
              name: "Delete",
              icon: "fa-regular fa-trash-can",
              standout: true,
              onClick: () => {
                setDeleteModal({
                  isOpen: true,
                  data: item,
                });
              },
            },
          ],
        },
      ],
    };
  });

  async function handleManageResponse(res) {
    if (!res) return toast.warning("No response received");

    if (res.status === 200 || res.status === 201) {
      const isNew = res.status === 201;

      queryClient.setQueryData(["table", filters, search], (state) => {
        if (!state) return state;
        const updatedData = JSON.parse(JSON.stringify(state?.data || []));

        if (isNew) {
          updatedData.unshift(res.data);
        } else {
          const index = updatedData.findIndex((item) => item._id === res.data._id);
          if (index !== -1) updatedData[index] = { ...updatedData[index], ...res.data };
        }

        return { ...state, data: updatedData };
      });

      if (isNew && filters.page !== 1) {
        setFilters((prev) => ({ ...prev, page: 1 }));
      } else {
        await queryClient.invalidateQueries(["table", filters, search]);
      }
      setManageDrawerOpen({ isOpen: false, data: {} });
    } else {
      toast.warning(res?.message || res?.error || "Something went wrong");
    }
  }

  const { mutate: deleteHandler, isPending: deletePending } = useMutation({
    mutationFn: async (payload) => {
      const res = await tableDelete({
        id: Array.isArray(payload) ? payload : [payload],
      });
      return {
        res,
        payload: Array.isArray(payload) ? payload : [payload],
      };
    },
    onSuccess: ({ res, payload }) => {
      if (res?.status === 200) {
        setDeleteModal({ isOpen: false, data: null });
        setSelectedItems([]);
        queryClient.setQueryData(["table", filters, search, selectFilterItem], (state) => {
          if (state) {
            let stateData = JSON.parse(JSON.stringify(state?.data));
            stateData = (stateData || [])?.filter((item) => !payload?.includes(item?._id));
            return { ...state, data: stateData };
          }
        });

        queryClient.invalidateQueries(["table", filters, search, selectFilterItem]);
      } else {
        toast.warning(res?.message || res?.error);
      }
    },
    onError: (error) => {
      console.log(error);
    },
  });

  function actionHandler(val) {
    switch (val) {
      case "blogDelete":
        setDeleteModal({
          isOpen: true,
          data: { _id: selectedItems },
        });
        break;

      default:
        break;
    }
  }
  const options = [
    {
      id: "blogDelete",
      icon: "fa-regular fa-trash-can",
      tooltip: "Blog Delete",
    },
  ];

  return (
    <div className="space-y-4 w-full flex flex-col pb-10">
      <div className="space-y-8">
        {/* {selectedAllFilters?.length > 0 && (
          <div className="flex gap-2 items-center flex-wrap mt-2">
            {selectedAllFilters?.map((item) => (
              <div
                key={item}
                className="flex items-center gap-2 bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium capitalize">
                {item}
                <button
                  onClick={() => {
                    setSelectedAllFilters((prev) => prev.filter((i) => i !== item));
                    setSelectedFilter(null);
                    setSelectFilterItem({});
                  }}
                  className="text-blue-600 hover:text-red-500 focus:outline-none">
                  ×
                </button>
              </div>
            ))}
          </div>
        )} */}

        <div className="font-semibold text-lg xls:text-sm xl:text-3xl text-primary text-right xls:text-left">{`Table Management`}</div>

        <div className="flex items-center gap-3 flex-col xls:flex-row w-full">
          <TableControls
            limit={filters?.limit}
            totalDocs={data?.metadata?.totalDocs}
            onLimitChange={(val) => handleFilterChange("limit", val, setFilters)}
            onSearch={(val) => {
              setKeyword(val);
              handleFilterChange("page", 1, setFilters);
            }}
          />
          {/* <TagFilter
            type={"post"}
            selectedFilter={selectedFilter}
            setSelectedFilter={setSelectedFilter}
            setSelectFilterItem={setSelectFilterItem}
            setSelectedAllFilters={setSelectedAllFilters}
            filterOptions={[
              { name: "Status", value: "status" },
              { name: "Date Range", value: "dateRange" },
              { name: "Category", value: "category" },
              { name: "Tags", value: "tags" },
            ]}
          /> */}
          {selectedItems?.length > 0 && (
            <div className="flex items-center gap-2">
              <ActionBar options={options} onChange={actionHandler} />
              <span className="flex items-center rounded-full bg-secondary text-surface-01 text-xs px-3 py-1 gap-2">
                {selectedItems?.length} Selected
                <button onClick={() => setSelectedItems([])} type="button" className="cursor-pointer">
                  <i className="fa-fw fa-light fa-xmark"></i>
                </button>
              </span>
            </div>
          )}
          <div className="relative xls:ml-auto flex-shrink-0">
              <Button
                buttonIcon={"fa-regular fa-plus"}
                buttonLabel={"Add Table"}
                buttonFunction={() => setManageDrawerOpen({ isOpen: true, data: {} })}
              />
          </div>
        </div>
        {/* <FilterTags />  buttonFunction={() => navigate("/admin/manage-blogs")}*/}
      </div>

      <Table
        tableHeadData={tableHeadData}
        tableData={tableData}
        totalDataCount={data?.metadata?.totalDocs}
        isLoder={isLoading}
        pageCount={data?.metadata?.totalPages}
        currentPage={filters?.page}
        onPageChange={(val) => {
          handleFilterChange("page", val, setFilters);
          setSelectedItems([]);
        }}
        // handleOrdering={(newData, oldData) => {
        //   postOrderingHandler(newData, oldData);
        // }}
        originalData={[...(data?.data || [])]}
        setOriginalData={(data) =>
          queryClient.setQueryData(["table", filters, search], (state) => {
            if (state) {
              return { ...state, data: data };
            }
          })
        }
        isFetching={isFetching}
        isOrderingDisabled={true}
        selectable
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        tableActionDropdownContainer={"!w-48"}
        showingData={getPaginationRange(filters?.page, filters?.limit, data?.metadata?.totalDocs)}
      />
      <DeleteModal
        open={deleteModal?.isOpen}
        loading={deletePending}
        onClose={() => setDeleteModal((pre) => ({ data: null, isOpen: false }))}
        deleteHandler={() => {
          deleteHandler(deleteModal?.data?._id);
        }}
      />
      <Drawer
        open={manageDrawerOpen.isOpen}
        onClose={() => setManageDrawerOpen({ isOpen: false, data: {} })}
        title={manageDrawerOpen.data?._id ? "Edit Table" : "Add New Table"}
        subtitle={manageDrawerOpen.data?._id ? "Update table information" : "Create a new table"}
        size="xl2"
        openFrom="right"
        animationDuration={0.3}
      >
        <ManageTable
          open={manageDrawerOpen.isOpen}
          close={() => setManageDrawerOpen({ isOpen: false, data: {} })}
          getResponse={handleManageResponse}
          data={manageDrawerOpen.data}
        />
      </Drawer>
      <Drawer
        open={drawerOpen.isOpen}
        onClose={() => setDrawerOpen({ isOpen: false, tableId: null })}
        title="Table Details"
        subtitle="View detailed information about this table"
        size="xl4"
        openFrom="right"
        animationDuration={0.3}
      >
        <TableDetails tableId={drawerOpen.tableId} />
      </Drawer>
    </div>
  );
};

export default TableList;
